/******************************************************************************
 * AdminPhotoCategoriesTab: settings tab to administer photo categories.
 *
 * A photo category is a single translatable entity carrying the list of
 * work-order (intervention) types it applies to and the min/max photo count.
 * The response exposes type ids only, so the tab loads the work-order types once
 * and builds an id -> label map for the Types column and the edit form.
 * Supports drag & drop reordering matching AdminCompinfos.
 *****************************************************************************/

/* Imports */
import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Button } from "@material-tailwind/react";
import { t } from "i18next";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";
/***/

/* Services */
import Loading from "../../../../../../components/loading/Loading";
import { Table } from "../../../../../../components/table/Table";
import { createListQuery, searchWithFilters } from "../../../../../../services/api/search";
import { api } from "../../../../../../services/api/api";
import { showApiErrorToast } from "../../../../../../services/api/error";
import EditCreateDrawer from "../../../../../../components/edit-create-drawer/EditCreateDrawer";
import PhotoCategoryForm from "../../../../../../components/data/photo-category/PhotoCategoryForm";
import PhotoCategoryListItem from "../../../../../../components/data/photo-category/PhotoCategoryListItem";
import { buildPhotoCategoryPayload, isValidCategory } from "../../../../../../components/data/photo-category/photoCategoryPayload";
/***/

/**
* AdminPhotoCategoriesTab: admin photo categories tab
*/
export default function AdminPhotoCategoriesTab() {
  const [categories, setCategories] = useState<EasyTech.PhotoCategory[] | null>(null);
  const [originalCategories, setOriginalCategories] = useState<EasyTech.PhotoCategory[] | null>(null);
  const [typeLabels, setTypeLabels] = useState<Record<string, string>>({});
  const [isReordering, setIsReordering] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const TABLE_HEAD = [
    { label: "#", orderKey: "order" },
    { label: "Label", orderKey: "label" },
    { label: "Types", orderKey: null },
    { label: "Min", orderKey: null },
    { label: "Max", orderKey: null },
    { label: "Application", orderKey: null },
    { label: "Active", orderKey: "enabled" },
    { label: "actions", orderKey: null }
  ];

  const TABLE_HEAD_REORDER = [
    { label: "#", orderKey: "order" },
    { label: "Label", orderKey: "label" },
    { label: "Types", orderKey: null },
    { label: "Min", orderKey: null },
    { label: "Max", orderKey: null },
    { label: "Application", orderKey: null },
    { label: "Active", orderKey: "enabled" }
  ];

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  /**
  * loadTypeLabels: fetch work-order types and build an id -> label map
  */
  const loadTypeLabels = useCallback(() => {
    searchWithFilters<EasyTech.WorkorderType>("workorder_types", {
      queries: [createListQuery("enabled", [true, false], "IN")]
    }, true, false)
      .then((ret) => {
        const map: Record<string, string> = {};
        (ret.data || []).forEach((type) => { map[type.id] = type.label; });
        setTypeLabels(map);
      })
      .catch((err) => console.error(err));
  }, []);

  /**
  * loadCategories: fetch photo categories (enabled and disabled)
  */
  const loadCategories = useCallback(() => {
    searchWithFilters<EasyTech.PhotoCategory>("photo_categories", {
      queries: [createListQuery("enabled", [true, false], "IN")],
      order: { by: "order", direction: "ASC" }
    }, true, false)
      .then((ret) => {
        const sorted = (ret.data as EasyTech.PhotoCategory[] || []).sort((a, b) => {
          if (a.order === undefined) return 1;
          if (b.order === undefined) return -1;
          return a.order - b.order;
        });
        setCategories(sorted);
        setOriginalCategories(sorted);
      })
      .catch((err) => {
        console.error(err);
        setCategories([]);
        setOriginalCategories([]);
      });
  }, []);

  /**
  * createCategory: create a photo category
  * @param data - category data to create
  */
  const createCategory = (data: EasyTech.PhotoCategory): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (isValidCategory(data)) {
        api.post("admin/photo_categories", buildPhotoCategoryPayload(data)).then(() => {
          loadCategories();
          toast.success("Photo category created");
          return resolve();
        }).catch((error) => {
          console.error(error);
          showApiErrorToast(error, { fallbackMessage: "Error while creating the photo category" });
          return reject();
        });
      } else {
        toast.error("Form is invalid");
        return reject();
      }
    });
  };
  /***/

  /**
   * handleDragEnd: reorder category list on drag completion
   */
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || !categories) return;

    if (active.id !== over.id) {
      const oldIndex = categories.findIndex((item) => item.id === active.id);
      const newIndex = categories.findIndex((item) => item.id === over.id);

      const newItems = arrayMove(categories, oldIndex, newIndex);
      setCategories(newItems);
    }
  };

  /**
   * toggleReordering: active/désactive le mode réorganisation
   */
  const toggleReordering = () => {
    if (isReordering && originalCategories) {
      setCategories([...originalCategories]);
    }
    setIsReordering(!isReordering);
  };

  /**
   * saveNewOrder: sauvegarde l'ordre des catégories sur le serveur
   */
  const saveNewOrder = async () => {
    if (!categories) return;

    setIsSaving(true);
    try {
      const entries = categories.map((cat, index) => ({
        id: cat.id as string,
        order: index + 1
      }));

      await api.post("admin/photo_categories/reorder", { entries });
      toast.success(t("settings:photoCategories:reorderSuccess", "Order saved"));
      setIsReordering(false);
      loadCategories();
    } catch (error) {
      console.error(error);
      showApiErrorToast(error, { fallbackMessage: t("settings:photoCategories:saveOrderError", "Error while saving the order") });
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    loadTypeLabels();
    loadCategories();
  }, [loadTypeLabels, loadCategories]);

  const resolveTypeLabels = (ids: string[]): string[] => (ids || []).map((id) => typeLabels[id] || id);

  return (
    <div>
      <div className="flex justify-between items-center my-4">
        {!isReordering ? (
          <div className="icon-buttons flex gap-2">
            <EditCreateDrawer data={({ label: "", translations: [] as EasyTech.Translation[], enabled: true, workOrderTypeIds: [], applicationField: "BOTH" } as EasyTech.PhotoCategory)}
              entityName="Photo category"
              FormComponent={PhotoCategoryForm}
              isNotEditable={true}
              onSubmit={createCategory}
              createButtonText="Create new Photo category" />
          </div>
        ) : <div />}

        {isReordering ? (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outlined"
              color="gray"
              className="normal-case font-thin"
              onClick={toggleReordering}
              disabled={isSaving}
            >
              {t("common:cancel")}
            </Button>
            <Button
              size="sm"
              variant="filled"
              color="blue"
              className="normal-case font-thin"
              onClick={saveNewOrder}
              disabled={isSaving}
            >
              {isSaving ? t("admin:buttons:saving") : t("admin:buttons:saveOrder")}
            </Button>
          </div>
        ) : (
          <Button
            variant="filled"
            color="blue"
            className="normal-case font-thin"
            onClick={toggleReordering}
            disabled={!categories || categories.length <= 1}
          >
            {t("common:reorder")}
          </Button>
        )}
      </div>

      <div className="my-6">
        {categories ? (
          isReordering ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={categories.map((c) => c.id || "")}
                strategy={verticalListSortingStrategy}
              >
                <Table
                  tableHead={TABLE_HEAD_REORDER}
                  emptyTable={categories.length === 0 ? "No photo categories" : false}
                >
                  {categories.map((category, index) => (
                    <PhotoCategoryListItem
                      key={category.id || index}
                      category={{ ...category, workOrderTypeLabels: resolveTypeLabels(category.workOrderTypeIds) }}
                      typeLabels={resolveTypeLabels(category.workOrderTypeIds)}
                      isLast={index === categories.length - 1}
                      isReordering={isReordering}
                      index={index + 1}
                      onChange={loadCategories}
                    />
                  ))}
                </Table>
              </SortableContext>
            </DndContext>
          ) : (
            <Table
              tableHead={TABLE_HEAD}
              emptyTable={categories.length === 0 ? "No photo categories" : false}
            >
              {categories.map((category, index) => (
                <PhotoCategoryListItem
                  key={category.id || index}
                  category={{ ...category, workOrderTypeLabels: resolveTypeLabels(category.workOrderTypeIds) }}
                  typeLabels={resolveTypeLabels(category.workOrderTypeIds)}
                  isLast={index === categories.length - 1}
                  index={index + 1}
                  onChange={loadCategories}
                />
              ))}
            </Table>
          )
        ) : (
          <Loading />
        )}
      </div>
    </div>
  );
}
/***/
